import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { FavoriteModule, FavoritesPreferences, PreferenceContextEnum, isFavoritesPreferences, UserPreferenceData } from '@scrambl/shared';
import { useOrganizationStore } from './organization.store';
import { useUserPreferencesStore } from './user-preferences.store';
import { createLogger } from '@/lib/logger';

const logger = createLogger('store/favorites.store');

export interface FavoriteItem {
  id: string;
name: string;
  type: 'entity' | 'project';
  logoUrl?: string;
  href: string;
  entityName?: string; // For projects, the name of the associated entity
  entityId?: string; // For projects, the ID of the associated entity
}

interface FavoritesState {
  // Core state
  favoriteEntities: string[];
  favoriteProjects: string[];
  favoriteModules: FavoriteModule[];
  displayOrder: 'recent' | 'alphabetical' | 'custom';
  maxNavigationDisplay: number;
  lastLoadedOrgId: string | null; // Track which org we last loaded for

  // Derived state for navigation display
  favoriteEntityItems: FavoriteItem[];
  favoriteProjectItems: FavoriteItem[];

  // Loading states
  isLoading: boolean;
  isUpdating: boolean;
  isOptimisticallyUpdating: boolean;
  _hasHydrated: boolean; // Track if persist has rehydrated

  // Actions
  loadFavorites: () => Promise<void>;
  toggleFavorite: (itemId: string, itemType: string, itemData?: { name: string; logoUrl?: string; parentName?: string; identifier?: string }) => Promise<void>;
  addFavorite: (itemId: string, itemType: 'entity' | 'project', itemData: { name: string; logoUrl?: string }) => Promise<void>;
  removeFavorite: (itemId: string, itemType: 'entity' | 'project') => Promise<void>;
  removeDeletedFavorite: (itemId: string, itemType: string) => Promise<void>;
  updateFavoritesList: (entities: Array<{ id: string; name: string; logoUrl?: string }>, projects: Array<{ id: string; name: string; logoUrl?: string }>) => void;
  isFavorited: (itemId: string, itemType: string) => boolean;

  // Internal helpers
  saveFavoritesToServer: () => Promise<UserPreferenceData | undefined>;
}

export const useFavoritesStore = create<FavoritesState>()(
  devtools(
    persist(
      (set, get) => {
        // Subscribe to user preferences store changes
        let unsubscribe: (() => void) | undefined;
        
        const initializeSubscription = () => {
          if (unsubscribe) return; // Already subscribed
          
          unsubscribe = useUserPreferencesStore.subscribe((state) => {
            if (state.isInitialized) {
              const favPrefs = state.getPreference(PreferenceContextEnum.FAVORITES);
              if (favPrefs?.preferences && isFavoritesPreferences(favPrefs.preferences)) {
                const currentState = get();
                // Only update if the data has actually changed to prevent loops
                const hasChanged =
                  JSON.stringify(currentState.favoriteEntities) !== JSON.stringify(favPrefs.preferences.favoriteEntities) ||
                  JSON.stringify(currentState.favoriteProjects) !== JSON.stringify(favPrefs.preferences.favoriteProjects) ||
                  JSON.stringify(currentState.favoriteModules) !== JSON.stringify(favPrefs.preferences.favoriteModules || []) ||
                  currentState.displayOrder !== favPrefs.preferences.displayOrder ||
                  currentState.maxNavigationDisplay !== favPrefs.preferences.maxNavigationDisplay;

                // Update only if data changed and not during optimistic update (which handles its own sync)
                if (hasChanged && !currentState.isOptimisticallyUpdating) {
                  set({
                    favoriteEntities: favPrefs.preferences.favoriteEntities || [],
                    favoriteProjects: favPrefs.preferences.favoriteProjects || [],
                    favoriteModules: favPrefs.preferences.favoriteModules || [],
                    displayOrder: favPrefs.preferences.displayOrder || 'recent',
                    maxNavigationDisplay: favPrefs.preferences.maxNavigationDisplay || 5
                  });
                }
              }
            }
          });
        };

        // Initialize subscription after a short delay to ensure user preferences store is ready
        setTimeout(initializeSubscription, 100);

        return {
          // Initial state
          favoriteEntities: [],
          favoriteProjects: [],
          favoriteModules: [],
          displayOrder: 'recent',
          maxNavigationDisplay: 5,
          lastLoadedOrgId: null,
          favoriteEntityItems: [],
          favoriteProjectItems: [],
          isLoading: false,
          isUpdating: false,
          isOptimisticallyUpdating: false,
          _hasHydrated: false,

          // Load favorites from centralized user preferences store
          loadFavorites: async () => {
            const userPrefsStore = useUserPreferencesStore.getState();
            
            // Wait for user preferences to be initialized
            if (!userPrefsStore.isInitialized) {
              return;
            }
            
            // Mark the load against the org we're actually in. Deriving this from
            // the FAVORITES preference's organizationId breaks for orgs with no
            // saved favorites (null) — lastLoadedOrgId then never matches the
            // current org and the previous org's persisted favorites keep showing.
            const currentOrgId = useOrganizationStore.getState().currentOrganization?.id || null;
            
            set({ isLoading: true });
            
            try {
              // Get favorites from centralized store
              const existingPrefs = userPrefsStore.getPreference(PreferenceContextEnum.FAVORITES);
              
              if (existingPrefs?.preferences && isFavoritesPreferences(existingPrefs.preferences)) {
                const favPrefs = existingPrefs.preferences;
                set({
                  favoriteEntities: favPrefs.favoriteEntities || [],
                  favoriteProjects: favPrefs.favoriteProjects || [],
                  favoriteModules: favPrefs.favoriteModules || [],
                  displayOrder: favPrefs.displayOrder || 'recent',
                  maxNavigationDisplay: favPrefs.maxNavigationDisplay || 5,
                  lastLoadedOrgId: currentOrgId,
                  isLoading: false
                });
              } else {
                // No existing preferences, use defaults without saving (to avoid API call)
                set({
                  favoriteEntities: [],
                  favoriteProjects: [],
                  favoriteModules: [],
                  displayOrder: 'recent',
                  maxNavigationDisplay: 5,
                  lastLoadedOrgId: currentOrgId,
                  isLoading: false
                });
              }
            } catch (error) {
              logger.error('[FavoritesStore] Failed to load favorites:', error);
              // Use defaults on error
              set({
                favoriteEntities: [],
                favoriteProjects: [],
                favoriteModules: [],
                displayOrder: 'recent',
                maxNavigationDisplay: 5,
                lastLoadedOrgId: currentOrgId,
                isLoading: false
              });
            }
          },

          // Save favorites to server using centralized user preferences store
          saveFavoritesToServer: async () => {
            const state = get();
            const userPrefsStore = useUserPreferencesStore.getState();
            
            try {
              const favoritesPrefs: FavoritesPreferences = {
                favoriteEntities: state.favoriteEntities,
                favoriteProjects: state.favoriteProjects,
                favoriteModules: state.favoriteModules,
                displayOrder: state.displayOrder,
                maxNavigationDisplay: state.maxNavigationDisplay
              };
              
              const savedPref = await userPrefsStore.savePreference(
                PreferenceContextEnum.FAVORITES,
                favoritesPrefs
              );
              
              // Return the saved preferences so we can sync with them
              return savedPref;
            } catch (error) {
              logger.error('Failed to save favorites:', error);
              throw error;
            }
          },

          // Toggle favorite status
          toggleFavorite: async (itemId: string, itemType: string, itemData?: { name: string; logoUrl?: string; parentName?: string; identifier?: string }) => {
            const state = get();
            const isCurrentlyFavorited = state.isFavorited(itemId, itemType);
            const isLegacyType = itemType === 'entity' || itemType === 'project';

            // Set optimistic updating flag to prevent subscription interference
            set({ isOptimisticallyUpdating: true });

            try {
              // Optimistic update - immediately update the UI
              if (isCurrentlyFavorited) {
                // Remove from favoriteModules
                const updatedModules = state.favoriteModules.filter(
                  m => !(m.id === itemId && m.type === itemType)
                );
                set({ favoriteModules: updatedModules });

                // Also remove from legacy arrays for entity/project
                if (isLegacyType) {
                  if (itemType === 'entity') {
                    set({
                      favoriteEntities: state.favoriteEntities.filter(id => id !== itemId),
                      favoriteEntityItems: state.favoriteEntityItems.filter(item => item.id !== itemId)
                    });
                  } else {
                    set({
                      favoriteProjects: state.favoriteProjects.filter(id => id !== itemId),
                      favoriteProjectItems: state.favoriteProjectItems.filter(item => item.id !== itemId)
                    });
                  }
                }
              } else if (itemData) {
                // Add to favoriteModules
                const newModule: FavoriteModule = {
                  id: itemId,
                  type: itemType,
                  name: itemData.name,
                  parentName: itemData.parentName,
                  identifier: itemData.identifier,
                };
                set({ favoriteModules: [...state.favoriteModules, newModule] });

                // Also add to legacy arrays for entity/project
                if (isLegacyType) {
                  if (itemType === 'entity') {
                    const updatedEntities = [...state.favoriteEntities, itemId];
                    const newItem: FavoriteItem = {
                      id: itemId,
                      name: itemData.name,
                      type: 'entity',
                      logoUrl: itemData.logoUrl,
                      href: `/entities/${itemId}`
                    };
                    set({
                      favoriteEntities: updatedEntities,
                      favoriteEntityItems: [...state.favoriteEntityItems, newItem].slice(0, state.maxNavigationDisplay)
                    });
                  } else {
                    const updatedProjects = [...state.favoriteProjects, itemId];
                    const newItem: FavoriteItem = {
                      id: itemId,
                      name: itemData.name,
                      type: 'project',
                      logoUrl: itemData.logoUrl,
                      href: `/projects/${itemId}`
                    };
                    set({
                      favoriteProjects: updatedProjects,
                      favoriteProjectItems: [...state.favoriteProjectItems, newItem].slice(0, state.maxNavigationDisplay)
                    });
                  }
                }
              }

              // Save to server with the updated state
              const savedPref = await state.saveFavoritesToServer();

              // Clear the optimistic updating flag so subscription can work again
              set({ isOptimisticallyUpdating: false });

              // Use the SAVED preference directly (don't read from store which might have stale data)
              if (savedPref?.preferences && isFavoritesPreferences(savedPref.preferences)) {
                const favPrefs = savedPref.preferences;
                const savedOrgId = savedPref.organizationId;
                set({
                  favoriteEntities: favPrefs.favoriteEntities || [],
                  favoriteProjects: favPrefs.favoriteProjects || [],
                  favoriteModules: favPrefs.favoriteModules || [],
                  displayOrder: favPrefs.displayOrder || 'recent',
                  maxNavigationDisplay: favPrefs.maxNavigationDisplay || 5,
                  lastLoadedOrgId: savedOrgId // Mark that we've loaded/saved for this org
                });
              }
            } catch (error) {
              logger.error('Failed to toggle favorite:', error);
              // Revert on error
              set({ isOptimisticallyUpdating: false });
              await state.loadFavorites();
              throw error;
            }
          },

          // Add favorite
          addFavorite: async (itemId: string, itemType: 'entity' | 'project', itemData: { name: string; logoUrl?: string }) => {
            set({ isUpdating: true });
            try {
              const currentState = get();
              
              if (itemType === 'entity') {
                const updatedEntities = [...currentState.favoriteEntities, itemId];
                set({ favoriteEntities: updatedEntities });
                
                // Update navigation items immediately
                const newItem: FavoriteItem = {
                  id: itemId,
                  name: itemData.name,
                  type: 'entity',
                  logoUrl: itemData.logoUrl,
                  href: `/entities/${itemId}`
                };
                set({ 
                  favoriteEntityItems: [...currentState.favoriteEntityItems, newItem].slice(0, currentState.maxNavigationDisplay)
                });
              } else {
                const updatedProjects = [...currentState.favoriteProjects, itemId];
                set({ favoriteProjects: updatedProjects });
                
                // Update navigation items immediately
                const newItem: FavoriteItem = {
                  id: itemId,
                  name: itemData.name,
                  type: 'project',
                  logoUrl: itemData.logoUrl,
                  href: `/projects/${itemId}`
                };
                set({ 
                  favoriteProjectItems: [...currentState.favoriteProjectItems, newItem].slice(0, currentState.maxNavigationDisplay)
                });
              }

              await currentState.saveFavoritesToServer();
            } catch (error) {
              logger.error('Failed to add favorite:', error);
              // Revert on error
              await get().loadFavorites();
              throw error;
            } finally {
              set({ isUpdating: false });
            }
          },

          // Remove favorite
          removeFavorite: async (itemId: string, itemType: 'entity' | 'project') => {
            set({ isUpdating: true });
            try {
              const currentState = get();
              
              if (itemType === 'entity') {
                const updatedEntities = currentState.favoriteEntities.filter(id => id !== itemId);
                set({ 
                  favoriteEntities: updatedEntities,
                  favoriteEntityItems: currentState.favoriteEntityItems.filter(item => item.id !== itemId)
                });
              } else {
                const updatedProjects = currentState.favoriteProjects.filter(id => id !== itemId);
                set({ 
                  favoriteProjects: updatedProjects,
                  favoriteProjectItems: currentState.favoriteProjectItems.filter(item => item.id !== itemId)
                });
              }

              await currentState.saveFavoritesToServer();
            } catch (error) {
              logger.error('Failed to remove favorite:', error);
              // Revert on error
              await get().loadFavorites();
              throw error;
            } finally {
              set({ isUpdating: false });
            }
          },

          // Remove a record deleted elsewhere (another page, window, or user).
          // Type comparison is normalized because legacy entity/project values
          // are lowercase while generic module favorites are usually uppercase.
          removeDeletedFavorite: async (itemId: string, itemType: string) => {
            const normalizedType = itemType.trim().toUpperCase();
            const currentState = get();
            const favoriteModules = currentState.favoriteModules.filter(
              module =>
                !(
                  module.id === itemId &&
                  module.type.trim().toUpperCase() === normalizedType
                )
            );
            const favoriteEntities = normalizedType === 'ENTITY'
              ? currentState.favoriteEntities.filter(id => id !== itemId)
              : currentState.favoriteEntities;
            const favoriteProjects = normalizedType === 'PROJECT'
              ? currentState.favoriteProjects.filter(id => id !== itemId)
              : currentState.favoriteProjects;
            const changed =
              favoriteModules.length !== currentState.favoriteModules.length ||
              favoriteEntities.length !== currentState.favoriteEntities.length ||
              favoriteProjects.length !== currentState.favoriteProjects.length;

            if (!changed) return;

            set({
              favoriteModules,
              favoriteEntities,
              favoriteProjects,
              favoriteEntityItems: normalizedType === 'ENTITY'
                ? currentState.favoriteEntityItems.filter(item => item.id !== itemId)
                : currentState.favoriteEntityItems,
              favoriteProjectItems: normalizedType === 'PROJECT'
                ? currentState.favoriteProjectItems.filter(item => item.id !== itemId)
                : currentState.favoriteProjectItems,
            });

            try {
              await get().saveFavoritesToServer();
            } catch (error) {
              // Keep the invalid favorite out of the current UI. The backend's
              // load-time sanitizer will permanently remove it on the next sync.
              logger.error('Failed to persist deleted favorite cleanup:', error);
            }
          },

          // Update favorites list with current entity/project data
          updateFavoritesList: (
            entities: Array<{ id: string; name: string; logoUrl?: string }>,
            projects: Array<{ id: string; name: string; logoUrl?: string; moduleType?: string | null; moduleId?: string | null }>
          ) => {
            const currentState = get();
            
            // Update entity items
            const favoriteEntityItems = currentState.favoriteEntities
              .map(entityId => {
                const entity = entities.find(e => e.id === entityId);
                if (!entity) return null;
                
                return {
                  id: entity.id,
                  name: entity.name,
                  type: 'entity' as const,
                  logoUrl: entity.logoUrl,
                  href: `/entities/${entity.id}`
                };
              })
              .filter((item) => item !== null)
              .slice(0, currentState.maxNavigationDisplay);

            // Update project items
            const favoriteProjectItems = currentState.favoriteProjects
              .map(projectId => {
                const project = projects.find(p => p.id === projectId);
                if (!project) return null;
                
                // Resolve entity name from the entities list if moduleType is ENTITY
                const linkedEntity = project.moduleType === 'ENTITY' && project.moduleId
                  ? entities.find(e => e.id === project.moduleId)
                  : undefined;

                return {
                  id: project.id,
                  name: project.name,
                  type: 'project' as const,
                  logoUrl: project.logoUrl,
                  href: `/projects/${project.id}`,
                  entityName: linkedEntity?.name,
                  entityId: linkedEntity?.id,
                };
              })
              .filter((item) => item !== null)
              .slice(0, currentState.maxNavigationDisplay);

            set({ favoriteEntityItems, favoriteProjectItems });
          },

          // Check if item is favorited
          isFavorited: (itemId: string, itemType: string) => {
            const currentState = get();
            // Check legacy arrays for entity/project
            if (itemType === 'entity' && currentState.favoriteEntities.includes(itemId)) return true;
            if (itemType === 'project' && currentState.favoriteProjects.includes(itemId)) return true;
            // Check generic favoriteModules array
            return currentState.favoriteModules.some(m => m.id === itemId && m.type === itemType);
          }
        }
      },
      {
        name: 'favorites-store',
        // Only persist the core data, not derived state
        partialize: (state) => ({
          favoriteEntities: state.favoriteEntities,
          favoriteProjects: state.favoriteProjects,
          favoriteModules: state.favoriteModules,
          displayOrder: state.displayOrder,
          maxNavigationDisplay: state.maxNavigationDisplay,
          lastLoadedOrgId: state.lastLoadedOrgId
        }),
        onRehydrateStorage: () => (state, error) => {
          if (error) {
            logger.error('[FavoritesStore] Hydration error:', error);
          } else if (state) {
            // Use setTimeout to ensure state is fully rehydrated before setting flag
            setTimeout(() => {
              useFavoritesStore.setState({ _hasHydrated: true });
            }, 0);
          }
        }
      }
    ),
    { name: 'FavoritesStore' }
  )
);
